<template>
  <div>
    <v-overlay
      :value="loading"
      absolute
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-overlay>
    <v-snackbar
      v-model="notification.show"
      :color="notification.color"
    >
      {{ notification.text }}
    </v-snackbar>
    <detection-report-filter v-model="filter" />
    <v-data-table
      :headers="headers"
      :items="blockedCards"
      :items-per-page="pagination.itemsPerPage"
      :loading="loading"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50], showFirstLastPage: true}"
      :server-items-length="totalItems"
      :options.sync="pagination"
      show-expand
    >
      <template v-slot:top>
        <export-button
          id="detection-report-button-export"
          @clickCsv="exportDetectionReport('csv')"
          @clickXls="exportDetectionReport('xls')"
        />
      </template>

      <template v-slot:expanded-item="{ item }">
        <td :colspan="headers.length">
          <detected-transactions-table
            :transactions="item.transactions"
            :admin-portal-url="adminPortalUrl"
            :transaction-type="item.transactionType"
          />
        </td>
      </template>

      <template v-slot:header.cardNumber="{ header }">
        <div>{{ header.text }}</div>
        <v-text-field
          v-model="cardNumberFilterValue"
          prepend-icon="mdi-filter-variant"
          type="text"
          clearable
          @click.stop
        />
      </template>
      <template v-slot:header.programCode="{ header }">
        <div>{{ header.text }}</div>
        <v-text-field
          v-model="programFilterValue"
          prepend-icon="mdi-filter-variant"
          type="text"
          clearable
          @click.stop
        />
      </template>
      <template v-slot:item.cardNumber="{ item }">
        <span>{{ item.cardNumber }}</span>
        <v-icon
          v-if="canEdit"
          small
          class="ml-2"
          @click="openExclusionDialog(item, true, false)"
        >
          mdi-cancel
        </v-icon>

        <v-icon
          v-if="canEdit"
          small
          class="ml-2"
          @click="openExclusionDialog(item, true, true)"
        >
          mdi-lock-open
        </v-icon>
      </template>
      <template v-slot:item.memberAccountId="{ item }">
        <span>{{ item.memberAccountId }}</span>
        <v-icon
          v-if="canEdit && item.memberAccountId && item.memberAccountId !== ''"
          small
          class="ml-2"
          @click="openExclusionDialog(item, false, false)"
        >
          mdi-cancel
        </v-icon>
        <v-icon
          v-if="canEdit && item.memberAccountId && item.memberAccountId !== ''"
          small
          class="ml-2"
          @click="openExclusionDialog(item, false, true)"
        >
          mdi-lock-open
        </v-icon>
      </template>
      <template v-slot:item.timeRange="{ item }">
        <!-- convert from minutes to hours -->
        {{ item.timeRange ? item.timeRange / 60 : '' }}
      </template>
      <template v-slot:item.lockingDateTime="{ item }">
        {{ getDateTimeInET(item.lockingDateTime) }}
      </template>
      <template v-slot:item.firstSuspiciousTransactionDateTime="{ item }">
        {{ getDateTimeInET(item.firstSuspiciousTransactionDateTime) }}
      </template>
      <template v-slot:item.lastSuspiciousTransactionDateTime="{ item }">
        {{ getDateTimeInET(item.lastSuspiciousTransactionDateTime) }}
      </template>
    </v-data-table>
    <v-dialog
      v-model="confirmExclusionDialog.show"
      max-width="400"
    >
      <v-card>
        <v-card-title class="headline">
          Confirm exclusion
        </v-card-title>

        <v-card-text>
          <p>
            Do you really want to exclude {{ confirmExclusionDialog.type }} {{ confirmExclusionDialog.value }}?
          </p>
          <p v-if="confirmExclusionDialog.unlock">
            By confirming you will also unlock the card, member account and all cards linked to the account.
          </p>
          <p>
            <v-form
              ref="form"
              v-model="excludeFormValid"
              lazy-validation
            >
              <v-text-field
                id="exclusion-confirmation-dialog-description'"
                v-model="confirmExclusionDialog.description"
                filled
                dense
                label="You can add Description to this exclusion"
                :rules="[v => v.length <= 100 || 'Max 100 characters']"
                counter="100"
              />
            </v-form>
          </p>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="primary"
            :loading="confirmExclusionDialog.loading"
            @click="exclude()"
          >
            <span v-if="!confirmExclusionDialog.unlock">Exclude</span>
            <span v-if="confirmExclusionDialog.unlock">Exclude and Unlock</span>
          </v-btn>

          <v-btn
            color="primary"
            text
            :loading="confirmExclusionDialog.loading"
            @click="confirmExclusionDialog.show = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { api } from '@/http/api'
  import dateUtils from '../util/dateUtils'
  import auth from '@/auth/auth'

  export default {
    name: 'DetectionReport',

    components: {
      DetectionReportFilter: () => import('../components/DetectionReportFilter'),
      DetectedTransactionsTable: () => import('../components/DetectedTransactionsTable'),
      ExportButton: () => import('../components/ExportButton'),
    },

    data () {
      return {
        loading: false,
        excludeFormValid: true,
        confirmExclusionDialog: {
          show: false,
          loading: false,
          unlock: false,
          detectionId: null,
          value: null,
          type: null,
          description: '',
        },
        pagination: {
          page: 1,
          itemsPerPage: 10,
          sortBy: ['lockingDateTime'],
          sortDesc: [true],
        },
        lastClickedColumn: '',
        totalItems: 0,
        headers: [
          {
            text: 'Card Number',
            value: 'cardNumber',
            class: 'detection-report-table-header-cardnumber filterable-table-header',
          },
          {
            text: 'Card Type',
            value: 'cardNumberType',
            class: 'detection-report-table-header-cardtype',
          },
          {
            text: 'Member Account ID',
            value: 'memberAccountId',
            class: 'detection-report-table-header-memberaccountid',
          },
          {
            text: 'Date & Time of Locking (ET)',
            value: 'lockingDateTime',
            class: 'detection-report-table-header-lockdatetime',
          },
          {
            text: 'Date & Time of First Suspicious Transaction (ET)',
            value: 'firstSuspiciousTransactionDateTime',
            class: 'detection-report-table-header-firstsatransactiondatetime',
          },
          {
            text: 'Date & Time of Last Suspicious Transaction (ET)',
            value: 'lastSuspiciousTransactionDateTime',
            class: 'detection-report-table-header-lastsatransactiondatetime',
          },
          {
            text: 'Program ID',
            align: 'from',
            value: 'programCode',
            class: 'detection-report-table-header-program filterable-table-header',
          },
          {
            text: 'Detection Rule: Transaction Type(# Hours, # Transactions)',
            value: 'detectionRule',
            class: 'detection-report-table-header-detectionRule',
          },
          {
            text: 'Current card status',
            value: 'currentCardStatus',
            class: 'detection-report-table-header-currentcardstatus',
            sortable: false,
          },
          {
            text: 'Current member account status',
            value: 'currentMemberAccountStatus',
            class: 'detection-report-table-header-currentmemberaccountstatus',
            sortable: false,
          },
          {
            text: '',
            value: 'data-table-expand',
            sortable: false,
          },
        ],
        blockedCards: [],
        notification: {
          show: false,
          text: '',
          color: 'success', // ['success', 'info', 'error']
        },
        programFilterValue: null,
        filter: {
          fromDate: dateUtils.dateETDateWeekAgoString(new Date()),
          fromTime: dateUtils.getETTimeString(new Date()),
          toDate: dateUtils.getETDateString(new Date()),
          toTime: dateUtils.getETTimeString(new Date()),
          isValid: true,
        },
        lastDataFetchIndex: 0,
        cardNumberFilterValue: null,
        adminPortalUrl: '#',
      }
    },

    computed: {
      canEdit: () => auth.canEdit(),
    },

    watch: {
      filter: {
        deep: true,

        handler () {
          // Reset to page 1 (new object reference) on any filter change.
          // The pagination watcher below performs the actual fetch, so sort/page
          // changes from the data table and date-range changes share one path.
          this.pagination = {
            page: 1,
            itemsPerPage: this.pagination.itemsPerPage || 10,
            sortBy: this.pagination.sortBy && this.pagination.sortBy.length
              ? this.pagination.sortBy : ['lockingDateTime'],
            sortDesc: this.pagination.sortDesc && this.pagination.sortDesc.length
              ? this.pagination.sortDesc : [true],
          }
        },
      },
      pagination: {
        deep: true,

        handler () {
          this.fetchDetectionReportData(
            this.filter.fromDate,
            this.filter.fromTime,
            this.filter.toDate,
            this.filter.toTime,
          )
        },
      },
      cardNumberFilterValue () {
        this.onSearchChanged()
      },
      programFilterValue () {
        this.onSearchChanged()
      },
    },

    mounted () {
      api.getConfig().then((config) => {
        this.adminPortalUrl = config.adminPortalUrl
      })

      // this.fetchDetectionReportData(this.filter.fromDate, this.filter.fromTime, this.filter.toDate, this.filter.toTime)
      // (LMT-1677, Observation 3) commented above line as two api calls are going when page loads. onPaginationChange also getting called while loading page
    },

    methods: {
      // Debounce card/program search and reset to page 1; the pagination watcher does the fetch
      // (server-side search), so totals and page count reflect the filtered result set.
      onSearchChanged () {
        clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => {
          this.pagination = { ...this.pagination, page: 1 }
        }, 400)
      },
      fetchDetectionReportData (fromDate, fromTime, toDate, toTime) {
        if (this.filter.isValid) {
          this.loading = true

          const fromTimestamp = dateUtils.getUTCTimestamp(fromDate, fromTime)
          const toTimestamp = dateUtils.getUTCTimestamp(toDate, toTime)
          const { page, itemsPerPage, sortBy, sortDesc } = this.pagination

          // Ensure sortBy is a string
          const sortColumn = Array.isArray(sortBy) && sortBy.length > 0 ? sortBy[0] : this.lastClickedColumn
          const sortDirection = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc

          api.getBlockedCards(fromTimestamp, toTimestamp, page, itemsPerPage, sortColumn, sortDirection,
            this.cardNumberFilterValue, this.programFilterValue)
            .then((response) => {
              this.blockedCards = response.detectionDtos
              this.totalItems = response.totalRecordsCount
              this.loading = false
            }).catch((err) => {
              this.showErrorMessage(err)
              this.loading = false
            })
        }
      },
      showErrorMessage (err) {
        if (err.data?.message) {
          this.notification = { show: true, text: err.data.message, color: 'error' }
        } else {
          this.notification = { show: true, text: 'Something went wrong.', color: 'error' }
        }
      },
      exportDetectionReport (format) {
        if (this.filter.isValid) {
          const fromTimestamp = dateUtils.getUTCTimestamp(this.filter.fromDate, this.filter.fromTime)
          const toTimestamp = dateUtils.getUTCTimestamp(this.filter.toDate, this.filter.toTime)
          api.exportDetectionReport(fromTimestamp, toTimestamp, format,
            this.cardNumberFilterValue, this.programFilterValue)
        } else {
          this.notification = { show: true, text: 'Filter is not valid', color: 'error' }
        }
      },
      cardNumberFilter (value) {
        if (!this.cardNumberFilterValue) {
          return true
        }
        if (!value) {
          return false
        }
        return value.toLowerCase().includes(this.cardNumberFilterValue.toLowerCase())
      },
      programFilter (value) {
        if (!this.programFilterValue) {
          return true
        }
        if (!value) {
          return false
        }
        return value.toLowerCase().includes(this.programFilterValue.toLowerCase())
      },
      getDateTimeInET (dateString) {
        return dateUtils.getDateTimeInET(dateString)
      },
      openExclusionDialog (item, isCard, unlock) {
        this.resetConfirmationDialog()

        this.confirmExclusionDialog.show = true
        this.confirmExclusionDialog.detectionId = item.id
        this.confirmExclusionDialog.unlock = unlock

        if (isCard) {
          this.confirmExclusionDialog.value = item.cardNumber
          this.confirmExclusionDialog.type = 'card'
        } else {
          this.confirmExclusionDialog.value = item.memberAccountId
          this.confirmExclusionDialog.type = 'account'
        }
      },
      exclude () {
        if (this.$refs.form.validate()) {
          this.confirmExclusionDialog.loading = true
          api.excludeFromRuleAndUnlock(
            this.confirmExclusionDialog.detectionId,
            this.confirmExclusionDialog.type,
            this.confirmExclusionDialog.description,
            this.confirmExclusionDialog.unlock,
          ).then(() => {
            this.confirmExclusionDialog.show = false
            this.notification = { show: true, text: 'Excluded', color: 'success' }
          }).catch(err => {
            this.showErrorMessage(err)
          }).finally(() => {
            this.confirmExclusionDialog.loading = false
          })
        }
      },
      resetConfirmationDialog () {
        this.confirmExclusionDialog.loading = false
        this.confirmExclusionDialog.show = false
        this.confirmExclusionDialog.detectionId = null
        this.confirmExclusionDialog.value = null
        this.confirmExclusionDialog.type = null
        this.confirmExclusionDialog.description = ''
      },
    },
  }
</script>
